import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import $api from "@/zapi-admin/shared/api/axios";

type TGenerationMode = "fast" | "slow";

type TUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  generationMode: TGenerationMode | null;
  effectiveGenerationMode: TGenerationMode;
};

const OPTIONS: { value: TGenerationMode; label: string; hint: string }[] = [
  { value: "fast", label: "Fast", hint: "Single-file skill from the codebase" },
  { value: "slow", label: "Slow", hint: "Active prompt revision" }
];

export function GenerationModePage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data: users, isLoading } = useQuery<TUser[]>({
    queryKey: ["admin-users-generation-mode"],
    queryFn: async () => (await $api.get<TUser[]>("/admin/users")).data
  });

  const { mutate: setMode, isPending } = useMutation({
    mutationFn: async ({ userId, generationMode }: { userId: number; generationMode: TGenerationMode | null }) =>
      (await $api.patch(`/admin/users/${userId}/generation-mode`, { generationMode })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users-generation-mode"] })
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users ?? [];
    return (users ?? []).filter((user) =>
      `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(term)
    );
  }, [users, search]);

  return (
    <div className="p-6">
      <h1 className="text-[20px] font-medium text-gray-900">Generation Mode</h1>
      <p className="mt-1 max-w-3xl text-[14px] text-gray-500">
        Which project-generation skill a user&apos;s runs use. <strong>Fast</strong> sends the single-file skill built
        into the backend: short, cheap, no prompt revision involved. <strong>Slow</strong> sends the active prompt
        revision, which is where the full business-case skill lives — those runs take hours and cost substantially
        more. A filled chip is the user&apos;s own choice; an outlined one is the environment default they still
        follow — click it to go back to following the environment.
      </p>

      <input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Filter by name or email"
        className="mt-4 w-[320px] rounded-lg border border-gray-200 px-3 py-2 text-[14px]"
        data-testid="generation-mode-search"
      />

      {isLoading ? (
        <div className="mt-6 text-[14px] text-gray-500">Loading users…</div>
      ) : (
        <table className="mt-4 w-full text-left text-[14px]">
          <thead>
            <tr className="border-b border-gray-200 text-gray-500">
              <th className="py-2 font-normal">User</th>
              <th className="py-2 font-normal">Email</th>
              <th className="py-2 font-normal">Mode</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.id} className="border-b border-gray-100" data-testid={`generation-mode-row-${user.id}`}>
                <td className="py-2">{`${user.firstName} ${user.lastName}`}</td>
                <td className="py-2 text-gray-500">{user.email}</td>
                <td className="py-2">
                  <div className="flex items-center gap-0.5 self-start rounded-lg border border-gray-200 p-0.5">
                    {OPTIONS.map((option) => {
                      const isEffective = user.effectiveGenerationMode === option.value;
                      const isExplicit = user.generationMode === option.value;
                      return (
                        <button
                          key={option.label}
                          type="button"
                          title={
                            isEffective && !isExplicit ? `${option.hint} — environment default` : option.hint
                          }
                          disabled={isPending}
                          onClick={() =>
                            setMode({ userId: user.id, generationMode: isExplicit ? null : option.value })
                          }
                          className={
                            isExplicit
                              ? "rounded-md bg-gray-900 px-3 py-1 text-[12px] leading-4 text-white"
                              : isEffective
                                ? "rounded-md border border-gray-900 px-[11px] py-[3px] text-[12px] leading-4 text-gray-900"
                                : "rounded-md px-3 py-1 text-[12px] leading-4 text-gray-500 hover:text-gray-900"
                          }
                          data-testid={`generation-mode-${user.id}-${option.value}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
